import { useCallback, useRef } from "react";
import { Button, useToast } from "@/ui";
import { useAuth } from "@/auth/AuthProvider";

/**
 * The signed-in person's avatar — the circle at the right of the header.
 *
 * It sits among the Identity fields but is NOT a branding token: branding is
 * one theme shared by everyone in a tenant, while this photo belongs to one
 * account and reaches nobody else. It rides the same Save button so the tab
 * behaves as one form, and `BrandingEditor` commits it through
 * `/auth/me/photo` rather than the branding PATCH.
 */

const ACCEPT = "image/png,image/jpeg,image/webp";
const MAX_BYTES = 5 * 1024 * 1024;

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(reader.error instanceof Error ? reader.error : new Error("Could not read file."));
    reader.readAsDataURL(file);
  });
}

/** The same two-letter fallback the header draws when there is no photo. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

export function ProfilePhotoField({
  staged,
  onStage,
  onClear,
}: {
  /** `undefined` — untouched. A string — a staged data URL. `null` — staged removal. */
  staged: string | null | undefined;
  onStage: (dataUrl: string) => void;
  onClear: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePick = useCallback(
    async (file: File) => {
      // Checked here as well as on the server: a 6MB photo should fail before
      // it is base64-encoded and pushed up the wire, not after.
      if (file.size > MAX_BYTES) {
        toast.error("That image is larger than 5 MB.");
        return;
      }
      try {
        onStage(await fileToDataUrl(file));
      } catch {
        toast.error("Could not read that image.");
      }
    },
    [onStage, toast],
  );

  if (!user) return null;

  const shown = staged === undefined ? user.photoUrl : staged;

  return (
    <div className="grid grid-cols-1 items-start gap-x-4 gap-y-1.5 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(9rem,14rem)_1fr]">
      <label className="text-sm font-medium text-ink sm:pt-2.5">Profile photo</label>
      <div className="flex items-start gap-2">
        <div className="max-w-xl min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            {shown ? (
              <img
                alt=""
                className="size-10 shrink-0 rounded-full border border-rule object-cover"
                src={shown}
              />
            ) : (
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-brand font-title text-[11px] font-semibold text-on-brand">
                {initials(user.name)}
              </span>
            )}
            <input
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handlePick(file);
                // Cleared so re-picking the same file fires onChange again.
                e.target.value = "";
              }}
              ref={fileInputRef}
              type="file"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              size="sm"
              type="button"
              variant="secondary"
            >
              Upload
            </Button>
            {shown ? (
              <Button onClick={onClear} size="sm" type="button" variant="ghost">
                Remove
              </Button>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-muted">
            Yours alone, not the organization&rsquo;s — shown in the circle at the top right. PNG,
            JPEG or WebP up to 5 MB, cropped square on upload.
          </p>
        </div>
      </div>
    </div>
  );
}
